/**
 * Public Insights Page - "Currency Analysis" (Data Product v3 / Farmgate F system).
 * FX metric stat-tiles, a translucent sticky control strip, and three chart tabs
 * (Exchange Rate · FX-Adjusted Comparison · Commodity vs Dollar). The naira is an
 * anchor series in navy - a rising NGN/USD rate reads as caution (amber), never a win.
 * All data comes from the same public hooks; PublicHero is left untouched.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  ComposedChart,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Link as RouterLink } from 'react-router-dom';
import { useFxSeries, useGlobalCommodities, useGlobalPrices, useCommodities, useLocalPrices, useCategories, useWorldSeriesCatalogue, useWorldReferenceBatch } from '../../services/queries';
import {
  WORLD_MONTHS, WORLD_UNITS, DEFAULT_WORLD_SERIES, toPriceMap, toSeriesMap,
  toPickerOptions, exchangeFeedIsEmpty, unitLabelFor, kgPerPublishedUnit,
  attributionOf,
} from '../../utils/worldReference';
import { WorldAttribution } from '../../components/common';
import { FX_SERIES, FX_LONG_RANGES, showsPegEra as pegEraVisible, thinForPlot, withRangeAvailability } from '../../utils/fxSeries';
import { isCommodityActive } from '../../utils/catalogStatus';
import { LoadingSkeleton, EmptyState } from '../../components/common';
import { fadeUp, MotionInView } from '../../utils/animations';
import { useThemeMode, numericSx, radius, MONO } from '../../context/ThemeContext';
import DeltaChip from '../../components/public/DeltaChip';
import { CountUp } from '../../hooks/useCounter';
import PublicHero from '../../components/public/PublicHero';
import SectionHeading from '../../components/public/SectionHeading';

// MUI-icon-compatible wrappers backed by Iconify Solar, so existing sx/fontSize usages still work
const iconifyFromSx = (sx = {}, fontSize) => {
  const sizeMap = { small: 18, medium: 24, large: 32, inherit: 24 };
  const size = sx.fontSize || (fontSize && sizeMap[fontSize]) || 20;
  return { width: size, height: size, color: sx.color };
};
const ArrowForward = ({ sx, fontSize }) => <Icon icon="solar:arrow-right-outline" {...iconifyFromSx(sx, fontSize)} />;
const ShowChart = ({ sx, fontSize }) => <Icon icon="solar:chart-2-outline" {...iconifyFromSx(sx, fontSize)} />;
const Layers = ({ sx, fontSize }) => <Icon icon="solar:layers-outline" {...iconifyFromSx(sx, fontSize)} />;
const BarChartIcon = ({ sx, fontSize }) => <Icon icon="solar:chart-outline" {...iconifyFromSx(sx, fontSize)} />;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Range values are DAY spans and the series is cut by date, not point count:
// the CBN feed posts daily rows, so a count-based slice would misrepresent
// the period. Matches the Commodities page.
const TIME_RANGES = [
  { label: '1M', value: 31 },
  { label: '3M', value: 92 },
  { label: '6M', value: 183 },
  { label: '1Y', value: 366 },
  { label: 'ALL', value: 99999 },
];

const CHART_TYPES = [
  { value: 'line', label: 'Line', icon: <ShowChart /> },
  { value: 'area', label: 'Area', icon: <Layers /> },
  { value: 'bar', label: 'Bar', icon: <BarChartIcon /> },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function PublicInsightsInner({ fxRates, globalCommodities, globalCommodityPrices: exchangePrices, commodities, localPrices, fxSeriesKey, onSeriesChange }) {
  // Same fallback as every other surface: the ICE and CBOT feed expired, so
  // this tab drew an empty chart under a "Source: CBN x CBOT" byline.
  const usingWorldReference = exchangeFeedIsEmpty(exchangePrices);
  const { data: worldCatalogue } = useWorldSeriesCatalogue({ enabled: usingWorldReference });
  const worldOptions = useMemo(() => toPickerOptions(worldCatalogue), [worldCatalogue]);
  const allSeriesIds = useMemo(() => worldOptions.map((o) => o.id), [worldOptions]);
  const { data: worldBatch } = useWorldReferenceBatch(
    allSeriesIds, WORLD_MONTHS, { enabled: usingWorldReference && allSeriesIds.length > 0 },
  );
  const worldPrices = useMemo(() => toPriceMap(worldBatch), [worldBatch]);
  const worldSeries = useMemo(() => toSeriesMap(worldBatch), [worldBatch]);
  const worldAttribution = attributionOf(worldBatch);
  const globalCommodityPrices = usingWorldReference ? worldPrices : exchangePrices;
  const [worldUnit, setWorldUnit] = useState('MT');
  const { palette } = useThemeMode();

  // State
  const [timeRange, setTimeRange] = useState(183);
  const fxSeries = FX_SERIES[fxSeriesKey];
  // The archive earns the long windows; NFEM cannot fill them, so they show
  // as disabled rather than disappearing when you switch back.
  const offeredRanges = useMemo(
    () =>
      withRangeAvailability(fxRates, [
        ...TIME_RANGES.filter((r) => r.value < 99999),
        ...FX_LONG_RANGES,
      ]),
    [fxRates]
  );

  // Switching series can leave the selection on a window the new series cannot
  // fill. Fall back to the widest one it can, so the chart is never labelled
  // with a span it does not actually cover.
  useEffect(() => {
    if (offeredRanges.find((r) => r.value === timeRange)?.disabled) {
      const fallback = [...offeredRanges].reverse().find((r) => !r.disabled);
      if (fallback) setTimeRange(fallback.value);
    }
  }, [offeredRanges, timeRange]);

  const [chartType, setChartType] = useState('line');
  const [activeTab, setActiveTab] = useState(0);
  const [fxCommodity, setFxCommodity] = useState('COM005'); // Commodity vs FX tab
  const [fxAdjustedCommodityId, setFxAdjustedCommodityId] = useState('GC002'); // FX-adjusted comparison tab

  // -----------------------------------------------------------------------
  // F design tokens (Home.jsx recipes)
  // -----------------------------------------------------------------------
  const cardSx = {
    bgcolor: palette.card,
    border: `1px solid ${palette.cardBorder}`,
    borderRadius: `${radius.card}px`,
    boxShadow: palette.shadowSm,
  };

  // "Updated · date" stamp, shared across cards/charts.
  const tsSx = { fontSize: '0.64rem', color: palette.textMuted, fontStyle: 'italic', mt: 0.6, letterSpacing: '0.02em' };

  // Recharts axis + grid (mono ticks, hairline grid).
  const axisStyle = { tick: { fontSize: 11, fill: palette.chartAxis, fontFamily: MONO }, axisLine: false, tickLine: false };
  const gridStyle = { stroke: palette.chartGrid, strokeDasharray: '3 3' };

  // Navy default-tooltip props (for the recharts default Tooltip).
  const tt = {
    contentStyle: { backgroundColor: palette.tooltipBg, border: `1px solid ${palette.tooltipBorder}`, borderRadius: 10, fontSize: '0.8rem', padding: '10px 14px', color: palette.tooltipText },
    labelStyle: { fontWeight: 600, color: palette.accentLight, marginBottom: 4 },
    itemStyle: { color: 'rgba(255,255,255,0.78)', fontSize: '0.78rem' },
  };

  // Navy custom-tooltip container + rows (dark navy bg, white mono figures).
  const ttBoxSx = { bgcolor: palette.tooltipBg, border: `1px solid ${palette.tooltipBorder}`, borderRadius: '10px', px: 1.75, py: 1.25, boxShadow: palette.shadowMd };
  const ttTitleSx = { fontSize: '0.72rem', fontWeight: 600, color: palette.accentLight, mb: 0.5 };
  const ttRowSx = { ...numericSx, fontSize: '0.78rem', color: palette.tooltipText, lineHeight: 1.6 };

  // Small F Select (used by both dual-axis tabs).
  const selectSx = {
    bgcolor: palette.card,
    color: palette.text,
    borderRadius: `${radius.control}px`,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.cardBorder },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.textMuted },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.accent },
    '& .MuiSvgIcon-root': { color: palette.textMuted },
  };
  const selectLabelSx = { color: palette.textSecondary, '&.Mui-focused': { color: palette.accent } };

  // Restyled legend: small dots + muted labels.
  const legendProps = {
    iconType: 'circle',
    iconSize: 8,
    wrapperStyle: { fontSize: 12 },
  };

  // -----------------------------------------------------------------------
  // Data processing (memoised)
  // -----------------------------------------------------------------------
  // Rows carrying no rate are dropped here rather than guarded at each use.
  // `rate` is nullable and everything downstream assumes a number: the
  // statistics take Math.max/min over the column, where a null reads as 0 and
  // flattens the period low; the correlation series multiplies by it; and the
  // tooltip formats it, where null throws. Mirrors CurrencyAnalysis.
  const pricedFxRates = useMemo(() => fxRates.filter((d) => d.rate != null), [fxRates]);

  const filteredFXData = useMemo(() => {
    if (timeRange >= 99999) return pricedFxRates;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - timeRange);
    const iso = cutoff.toISOString().slice(0, 10);
    return pricedFxRates.filter((d) => d.date >= iso);
  }, [pricedFxRates, timeRange]);

  // Axis date format adapts to the visible span: day + month inside half a
  // year, month + year beyond it (so ALL shows year boundaries clearly).
  const chartSpanDays = useMemo(() => {
    if (filteredFXData.length < 2) return 0;
    return (new Date(filteredFXData[filteredFXData.length - 1].date) - new Date(filteredFXData[0].date)) / 86400000;
  }, [filteredFXData]);
  const fmtAxisDate = (d) => {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    if (chartSpanDays > 180) {
      const m = dt.toLocaleDateString('en-GB', { month: 'short' });
      return `${m} '${String(dt.getFullYear()).slice(2)}`;
    }
    return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const stats = useMemo(() => {
    if (filteredFXData.length < 2) return null;
    const rates = filteredFXData.map((d) => d.rate);
    const latest = rates[rates.length - 1];
    const previous = rates[rates.length - 2];
    const first = rates[0];
    const high = Math.max(...rates);
    const low = Math.min(...rates);
    const avg = rates.reduce((a, b) => a + b, 0) / rates.length;
    const highDate = filteredFXData[rates.indexOf(high)]?.date;
    const lowDate = filteredFXData[rates.indexOf(low)]?.date;

    return {
      highDate,
      lowDate,
      current: latest,
      previous,
      periodStart: first,
      dayChange: latest - previous,
      dayChangePercent: ((latest - previous) / previous * 100).toFixed(2),
      periodChange: latest - first,
      periodChangePercent: ((latest - first) / first * 100).toFixed(2),
      high,
      low,
      avg: Math.round(avg),
      volatility: ((high - low) / avg * 100).toFixed(2),
    };
  }, [filteredFXData]);

  const chartData = useMemo(() => {
    // Thinned for drawing only; the summary figures still read the full series.
    const plotted = thinForPlot(filteredFXData);
    return plotted.map((item, index) => {
      const prevRate = index > 0 ? plotted[index - 1].rate : item.rate;
      const change = item.rate - prevRate;
      const changePercent = (change / prevRate * 100).toFixed(2);
      return {
        date: item.date,
        fullDate: item.date,
        rate: item.rate,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
        change,
        changePercent: parseFloat(changePercent),
      };
    });
  }, [filteredFXData]);

  const fxAdjustedCommodity = useMemo(() => {
    if (usingWorldReference) {
      const o = worldOptions.find((x) => x.id === fxAdjustedCommodityId) || worldOptions[0];
      return o ? { id: o.id, name: o.label, unit: o.unit, exchange: 'World Bank Pink Sheet' } : undefined;
    }
    return globalCommodities.find((c) => c.id === fxAdjustedCommodityId) || globalCommodities[0];
  }, [usingWorldReference, worldOptions, globalCommodities, fxAdjustedCommodityId]);

  const fxAdjustedUnitSuffix = useMemo(() => {
    if (usingWorldReference) {
      const chosen = WORLD_UNITS.find((u) => u.value === worldUnit);
      return chosen ? chosen.suffix.replace('/', '') : '';
    }
    const unit = fxAdjustedCommodity?.unit || 'USD';
    const parts = unit.split('/');
    return parts[1] ? parts[1].toLowerCase() : '';
  }, [usingWorldReference, worldUnit, fxAdjustedCommodity]);

  // A selection from the exchange id space resolves to nothing here.
  useEffect(() => {
    if (!usingWorldReference || allSeriesIds.length === 0) return;
    if (!allSeriesIds.includes(fxAdjustedCommodityId)) {
      setFxAdjustedCommodityId(
        allSeriesIds.includes(DEFAULT_WORLD_SERIES) ? DEFAULT_WORLD_SERIES : allSeriesIds[0],
      );
    }
  }, [usingWorldReference, allSeriesIds, fxAdjustedCommodityId]);

  const correlationData = useMemo(() => {
    // Joined BY DATE: the FX and commodity series have different lengths and
    // holidays, so an index join would pair prices from different days.
    //
    // A monthly world price cannot meet a daily rate on an exact day; they
    // would coincide on the first of each month and the chart would be twelve
    // lonely points a year. A monthly average applies to its whole month, so
    // every day in August carries August's figure: the USD line steps monthly
    // while the naira line moves daily on top of it.
    const key = (d) => (usingWorldReference ? String(d).slice(0, 7) : d);
    const closeBy = new Map((globalCommodityPrices[fxAdjustedCommodityId] || []).map((p) => [key(p.date), p.close]));
    const kgPerNative = kgPerPublishedUnit(worldSeries[fxAdjustedCommodityId]?.unit);
    const toDisplay = (v) => {
      if (v == null) return null;
      if (!usingWorldReference || !kgPerNative) return v;
      const perKg = v / kgPerNative;
      if (worldUnit === 'kg') return perKg;
      if (worldUnit === 'lb') return perKg * 0.453592;
      return perKg * 1000;
    };
    return filteredFXData.map((fx) => {
      const close = toDisplay(closeBy.get(key(fx.date)));
      const ngn = close != null && fx.rate != null ? close * fx.rate : null;
      return {
        date: fx.date,
        fxRate: fx.rate,
        usdPrice: close ?? null,
        ngnPrice: ngn != null ? Math.round(ngn) : null,
      };
    });
  }, [globalCommodityPrices, filteredFXData, fxAdjustedCommodityId, usingWorldReference, worldSeries, worldUnit]);

  // Commodity Price vs Dollar Rate (dual-axis, filterable by local commodity)
  const commodityVsFxData = useMemo(() => {
    // Weekly local prices paired with the FX rate prevailing that week (the
    // last CBN close on or before the upload date). Exact-date matching
    // would drop weeks whenever the cadences differ or the upload day is a
    // market holiday.
    let cutoffIso = null;
    if (timeRange < 99999) {
      const c = new Date();
      c.setDate(c.getDate() - timeRange);
      cutoffIso = c.toISOString().slice(0, 10);
    }
    const pricesByWeek = {};
    localPrices
      .filter((p) => p.commodityId === fxCommodity)
      .forEach((p) => {
        if (cutoffIso && p.date < cutoffIso) return;
        if (!pricesByWeek[p.date]) pricesByWeek[p.date] = { total: 0, count: 0 };
        pricesByWeek[p.date].total += p.price;
        pricesByWeek[p.date].count += 1;
      });
    const rateAsOf = (date) => {
      let r = null;
      for (const fx of fxRates) {
        if (fx.date > date) break;
        r = fx.rate;
      }
      return r;
    };
    // Weeks BEFORE the first stored CBN rate keep fxRate null: the price bars
    // still plot for them and the rate line simply starts where FX history
    // begins - no data is hidden and none is fabricated.
    return Object.keys(pricesByWeek)
      .sort()
      .map((date) => ({
        date,
        fxRate: rateAsOf(date),
        price: Math.round(pricesByWeek[date].total / pricesByWeek[date].count),
      }));
  }, [fxRates, localPrices, timeRange, fxCommodity]);

  const { data: commodityCategories = [] } = useCategories();
  const isRetired = (c) => !isCommodityActive(c, commodityCategories);
  const fxCommodityRec = commodities.find((c) => c.id === fxCommodity);
  const fxCommodityName = (fxCommodityRec?.name || 'Commodity') + (fxCommodityRec && isRetired(fxCommodityRec) ? ' (retired)' : '');

  // Available local commodities that have price data
  const localCommoditiesWithData = useMemo(() => {
    const ids = new Set(localPrices.map((p) => p.commodityId));
    // Actives first so the auto-picked default is never a retired commodity;
    // retired ones stay pickable for history, labeled below.
    const retired = (c) => !isCommodityActive(c, commodityCategories);
    return commodities
      .filter((c) => ids.has(c.id))
      .sort((a, b) => Number(retired(a)) - Number(retired(b)));
  }, [commodities, localPrices, commodityCategories]);

  // Keep the Commodity vs Dollar selection on a commodity that actually has
  // local price data (the hardcoded default may have none).
  useEffect(() => {
    if (localCommoditiesWithData.length > 0 && !localCommoditiesWithData.some((c) => c.id === fxCommodity)) {
      setFxCommodity(localCommoditiesWithData[0].id);
    }
  }, [localCommoditiesWithData, fxCommodity]);

  const stampDate = fmtDate(fxRates[fxRates.length - 1]?.date);

  // Provenance of the FX series (the daily feed tags each row with its source).
  const fxSource = fxRates[fxRates.length - 1]?.source || '';
  const fxSourceLabel = fxSource.startsWith('external:cbn-nfem')
    ? 'CBN (NFEM)'
    : fxSource.startsWith('external:cbn-official')
      ? 'CBN (official)'
      : fxSource.startsWith('indicative')
        ? 'Indicative (not CBN)'
        : 'CBN';

  // -----------------------------------------------------------------------
  // Shared bits
  // -----------------------------------------------------------------------
  const figSx = { ...numericSx, fontWeight: 600, fontSize: { xs: '1.35rem', md: '1.55rem' }, color: palette.text, lineHeight: 1.2, letterSpacing: '-0.02em' };
  const labelSx = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: palette.textMuted };
  const cardTitleSx = { fontWeight: 700, color: palette.text, fontSize: '1.05rem', letterSpacing: '-0.01em' };
  const statMutedSx = { fontSize: '0.72rem', color: palette.textMuted, fontWeight: 500 };

  // F stat tile - mono figure + muted label + "Updated ·" stamp (no translateY hover).
  const StatTile = ({ label, children, chip }) => (
    <Box
      sx={{
        ...cardSx,
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.7,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': { borderColor: palette.textMuted, boxShadow: palette.shadowMd },
      }}
    >
      <Typography sx={labelSx}>{label}</Typography>
      {children}
      {chip ? <Box sx={{ display: 'flex' }}>{chip}</Box> : null}
      <Typography sx={{ ...tsSx, mt: 'auto' }}>Updated · {stampDate}</Typography>
    </Box>
  );

  const primaryBtnSx = {
    bgcolor: palette.accent,
    color: '#fff',
    fontWeight: 600,
    px: 3.25,
    py: 1.35,
    fontSize: '0.95rem',
    borderRadius: '10px',
    whiteSpace: 'nowrap',
    '&:hover': { bgcolor: '#1C9550', boxShadow: palette.shadowMd },
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.bg }}>
      {/* Hero (untouched) */}
      <PublicHero
        eyebrow="Currency Intelligence"
        title="Currency Analysis"
        subtitle="Daily FX tracking and commodity price analysis for the Nigerian market, with data-driven intelligence for informed decisions."
      />

      {/* ==============================================================
          METRIC STAT TILES (4)
          ============================================================== */}
      {stats && (
        <Box component="section" sx={{ bgcolor: palette.bg, pt: { xs: 6, md: 8 }, pb: { xs: 2, md: 3 } }}>
          <Container maxWidth="lg">
            <SectionHeading
              number="01"
              eyebrow="FX Overview"
              title="The Naira at a Glance"
              subtitle="Daily NGN/USD closes from CBN's official market window (NFEM), refreshed twice a day."
            />
            <MotionInView variants={fadeUp}>
              <Grid container spacing={2.5} sx={{ mt: { xs: 0.5, md: 1 } }}>
                <Grid item xs={6} md={3}>
                  <StatTile label="Current FX Rate">
                    <CountUp value={stats.current} prefix="₦" sx={figSx} />
                    <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted }}>NGN per USD · CBN NFEM</Typography>
                  </StatTile>
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile
                    label="Daily Change"
                    chip={
                      // A rising NGN/USD rate = a weaker naira → caution (amber), never green.
                      <DeltaChip value={Number(stats.dayChangePercent)} caution={stats.dayChange > 0} decimals={2} size="sm" />
                    }
                  >
                    <CountUp
                      value={stats.dayChange}
                      format={(n) => `${n >= 0 ? '+' : '−'}₦${Math.abs(Math.round(n)).toLocaleString()}`}
                      sx={figSx}
                    />
                    <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted }}>vs previous close</Typography>
                  </StatTile>
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile label="Period High">
                    <CountUp value={stats.high} prefix="₦" sx={figSx} />
                    <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted }}>{stats.highDate ? `on ${fmtDate(stats.highDate)}` : 'in the selected range'}</Typography>
                  </StatTile>
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile label="Period Low">
                    <CountUp value={stats.low} prefix="₦" sx={figSx} />
                    <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted }}>{stats.lowDate ? `on ${fmtDate(stats.lowDate)}` : 'in the selected range'}</Typography>
                  </StatTile>
                </Grid>
              </Grid>
            </MotionInView>
          </Container>
        </Box>
      )}

      {/* ==============================================================
          SECTION 02 HEADING -- the analysis tabs share one date range
          ============================================================== */}
      <Box component="section" sx={{ bgcolor: palette.bg, pt: { xs: 4, md: 5 }, pb: { xs: 2.5, md: 3 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            number="02"
            eyebrow="Analysis"
            title="Rate & Commodity Charts"
            subtitle="Three lenses on one date range: the naira itself, global prices adjusted by the rate, and local prices against the dollar."
          />
        </Container>
      </Box>

      {/* ==============================================================
          STICKY CONTROL STRIP (translucent white + blur + bottom hairline)
          ============================================================== */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.82)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${palette.divider}`,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
            {/* Rate series - which CBN series the FX views read from. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography sx={labelSx}>Series</Typography>
              <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }} role="group" aria-label="Exchange rate series">
                {Object.entries(FX_SERIES).map(([key, cfg]) => {
                  const active = fxSeriesKey === key;
                  return (
                    <Button
                      key={key}
                      onClick={() => onSeriesChange(key)}
                      disableElevation
                      aria-pressed={active}
                      sx={{
                        minWidth: 46,
                        px: 1.2,
                        py: 0.5,
                        fontFamily: MONO,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        borderRadius: '8px',
                        textTransform: 'none',
                        color: active ? '#fff' : palette.textSecondary,
                        bgcolor: active ? palette.accent : 'transparent',
                        border: `1px solid ${active ? palette.accent : palette.cardBorder}`,
                        '&:hover': { bgcolor: active ? palette.accent : palette.surfaceHover },
                      }}
                    >
                      {cfg.short}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Time Range - mono range buttons, active green bg white text */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography sx={labelSx}>Range</Typography>
              <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
                {offeredRanges.map((r) => {
                  const active = timeRange === r.value;
                  return (
                    <Button
                      key={r.label}
                      onClick={() => setTimeRange(r.value)}
                      disableElevation
                      disabled={r.disabled}
                      title={r.disabled ? `${fxSeries.label} does not go back that far` : undefined}
                      sx={{
                        minWidth: 46,
                        px: 1.2,
                        py: 0.5,
                        fontFamily: MONO,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        borderRadius: '8px',
                        color: active ? '#fff' : palette.textSecondary,
                        bgcolor: active ? palette.accent : palette.surface,
                        border: `1px solid ${active ? palette.accent : palette.cardBorder}`,
                        '&:hover': {
                          bgcolor: active ? '#1C9550' : palette.surfaceHover,
                          borderColor: active ? '#1C9550' : palette.textMuted,
                        },
                      }}
                    >
                      {r.label}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Chart Type - F ghost toggles */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: { md: 'auto' } }}>
              <Typography sx={labelSx}>Chart</Typography>
              <Box sx={{ display: 'inline-flex', border: `1px solid ${palette.cardBorder}`, borderRadius: `${radius.control}px`, overflow: 'hidden', bgcolor: palette.card }}>
                {CHART_TYPES.map((t, i) => {
                  const active = chartType === t.value;
                  return (
                    <Button
                      key={t.value}
                      onClick={() => setChartType(t.value)}
                      title={t.label}
                      aria-label={t.label}
                      disableElevation
                      sx={{
                        minWidth: 42,
                        px: 1.1,
                        py: 0.5,
                        borderRadius: 0,
                        borderLeft: i === 0 ? 'none' : `1px solid ${palette.cardBorder}`,
                        color: active ? palette.accent : palette.textMuted,
                        bgcolor: active ? palette.accentBg : 'transparent',
                        '&:hover': { bgcolor: active ? palette.accentBg : palette.surface },
                      }}
                    >
                      {t.icon}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ==============================================================
          TAB BAR
          ============================================================== */}
      <Box sx={{ bgcolor: palette.bg, borderBottom: `1px solid ${palette.divider}` }}>
        <Container maxWidth="lg">
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                minHeight: 52,
                color: palette.textSecondary,
              },
              '& .Mui-selected': { color: palette.accent },
              '& .MuiTabs-indicator': { backgroundColor: palette.accent },
            }}
          >
            <Tab label="Exchange Rate" />
            <Tab label="FX-Adjusted Comparison" />
            <Tab label="Commodity vs Dollar" />
          </Tabs>
        </Container>
      </Box>

      {/* ==============================================================
          TAB 0 -- Exchange Rate
          ============================================================== */}
      {activeTab === 0 && (
        <Box sx={{ bgcolor: palette.bg, py: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg">
            <MotionInView variants={fadeUp}>
              <Grid container spacing={3}>
                {/* Chart */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                    <Typography sx={cardTitleSx}>NGN/USD Exchange Rate</Typography>
                    <Typography sx={{ color: palette.textMuted, fontSize: '0.82rem', mt: 0.3 }}>
                      {fxSeries.blurb}
                    </Typography>
                    {pegEraVisible(fxSeriesKey, filteredFXData) && (
                      <Box
                        role="note"
                        sx={{
                          mt: 1.25, px: 1.5, py: 1, borderRadius: '10px',
                          bgcolor: palette.warningBg, border: `1px solid ${palette.warning}33`,
                        }}
                      >
                        <Typography sx={{ fontSize: '0.76rem', color: palette.text, lineHeight: 1.5 }}>
                          <strong>Before June 2023 this was an administered rate.</strong> It held
                          flat for years at levels most buyers could not actually obtain, while the
                          parallel market traded far higher. Read the early years as policy, not as
                          market prices.
                        </Typography>
                      </Box>
                    )}
                    <Typography sx={tsSx}>Source: {fxSourceLabel}, {stampDate}</Typography>
                    <Box sx={{ height: { xs: 300, md: 380 }, mt: 2 }}>
                      {(fxRates && fxRates.length > 0) ? (
                        <ResponsiveContainer width="100%" height="100%">
                          {(() => {
                            const FxChart = chartType === 'bar' ? BarChart : chartType === 'line' ? LineChart : AreaChart;
                            return (
                              <FxChart data={chartData}>
                                <defs>
                                  <linearGradient id="fxNavyArea" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={palette.navy} stopOpacity={0.2} />
                                    <stop offset="95%" stopColor={palette.navy} stopOpacity={0.02} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid {...gridStyle} />
                                <XAxis dataKey="date" {...axisStyle} minTickGap={28} tickFormatter={fmtAxisDate} />
                                <YAxis
                                  {...axisStyle}
                                  domain={['dataMin - 30', 'dataMax + 30']}
                                  tickFormatter={(v) => `₦${v}`}
                                />
                                <RechartsTooltip
                                  cursor={{ stroke: palette.chartAxis, strokeWidth: 1, strokeDasharray: '3 3' }}
                                  content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const d = payload[0].payload;
                                    const rose = d.change >= 0;
                                    return (
                                      <Box sx={ttBoxSx}>
                                        <Typography sx={ttTitleSx}>{fmtDate(d.fullDate)}</Typography>
                                        <Typography sx={ttRowSx}>Rate: {d.rate != null ? `₦${d.rate.toLocaleString()}` : '-'}</Typography>
                                        {d.high != null && (
                                          <Typography sx={ttRowSx}>High: ₦{d.high.toLocaleString()}</Typography>
                                        )}
                                        {d.low != null && (
                                          <Typography sx={ttRowSx}>Low: ₦{d.low.toLocaleString()}</Typography>
                                        )}
                                        <Typography sx={{ ...ttRowSx, color: rose ? '#F4C77B' : palette.accentLight }}>
                                          Change: {rose ? '+' : ''}{d.changePercent}%
                                        </Typography>
                                      </Box>
                                    );
                                  }}
                                />
                                <ReferenceLine
                                  y={stats?.avg}
                                  stroke={palette.textMuted}
                                  strokeDasharray="6 4"
                                  label={{
                                    value: `Avg ₦${stats?.avg?.toLocaleString()}`,
                                    fill: palette.textMuted,
                                    fontSize: 10,
                                    fontFamily: MONO,
                                    position: 'right',
                                  }}
                                />
                                {chartType === 'bar' ? (
                                  <Bar dataKey="rate" fill={palette.navy} radius={[4, 4, 0, 0]} />
                                ) : chartType === 'line' ? (
                                  <Line type="monotone" dataKey="rate" stroke={palette.navy} strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                                ) : (
                                  <Area type="monotone" dataKey="rate" stroke={palette.navy} strokeWidth={2.5} fill="url(#fxNavyArea)" dot={false} activeDot={{ r: 5, fill: palette.navy, strokeWidth: 2, stroke: palette.chartCard }} />
                                )}
                              </FxChart>
                            );
                          })()}
                        </ResponsiveContainer>
                      ) : (
                        <EmptyState
                          compact
                          icon={<Icon icon="solar:chart-2-linear" width={24} height={24} />}
                          title="No exchange-rate data yet"
                          description="NGN/USD history will appear here once rates are published."
                          sx={{ height: { xs: 300, md: 380 } }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Rate Statistics sidebar */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                    <Typography sx={cardTitleSx}>Rate Statistics</Typography>

                    {/* Period Change */}
                    <Box>
                      <Typography sx={statMutedSx}>Period Change</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1.25rem', color: stats ? palette.text : palette.textMuted }}>
                          {stats ? `${stats.periodChange >= 0 ? '+' : '−'}₦${Math.abs(Math.round(stats.periodChange)).toLocaleString()}` : '-'}
                        </Typography>
                        {stats && <DeltaChip value={Number(stats.periodChangePercent)} caution={stats.periodChange > 0} decimals={2} size="sm" />}
                      </Box>
                    </Box>

                    <Box sx={{ height: '1px', bgcolor: palette.divider }} />

                    {/* Volatility */}
                    <Box>
                      <Typography sx={statMutedSx}>Volatility</Typography>
                      <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1.25rem', color: stats ? palette.text : palette.textMuted, mt: 0.5 }}>
                        {stats ? `${stats.volatility}%` : '-'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted, mt: 0.25 }}>
                        High–Low range vs average
                      </Typography>
                    </Box>

                    <Box sx={{ height: '1px', bgcolor: palette.divider }} />

                    {/* Period Average */}
                    <Box>
                      <Typography sx={statMutedSx}>Period Average</Typography>
                      <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1.25rem', color: stats ? palette.text : palette.textMuted, mt: 0.5 }}>
                        {stats ? `₦${stats.avg?.toLocaleString()}` : '-'}
                      </Typography>
                    </Box>

                    <Box sx={{ height: '1px', bgcolor: palette.divider }} />

                    {/* Period Range */}
                    <Box>
                      <Typography sx={statMutedSx}>Period Range</Typography>
                      <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1.25rem', color: stats ? palette.text : palette.textMuted, mt: 0.5 }}>
                        {stats ? `₦${Math.round(stats.high - stats.low).toLocaleString()}` : '-'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted, mt: 0.25 }}>
                        {stats ? `₦${stats.low.toLocaleString()} to ₦${stats.high.toLocaleString()}` : ''}
                      </Typography>
                    </Box>

                    <Box sx={{ height: '1px', bgcolor: palette.divider }} />

                    {/* Trading days behind the numbers */}
                    <Box>
                      <Typography sx={statMutedSx}>Trading Days</Typography>
                      <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1.25rem', color: palette.text, mt: 0.5 }}>
                        {filteredFXData.length}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted, mt: 0.25 }}>
                        CBN closes in the selected range
                      </Typography>
                    </Box>

                    <Typography sx={{ ...tsSx, mt: 'auto' }}>Source: {fxSourceLabel} · updated daily</Typography>
                  </Box>
                </Grid>
              </Grid>
            </MotionInView>
          </Container>
        </Box>
      )}

      {/* ==============================================================
          TAB 1 -- FX-Adjusted Comparison (dual-axis)
          ============================================================== */}
      {activeTab === 1 && (
        <Box sx={{ bgcolor: palette.bg, py: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg">
            <MotionInView variants={fadeUp}>
              <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography sx={cardTitleSx}>FX-Adjusted Commodity Comparison</Typography>
                    <Typography sx={{ color: palette.textMuted, fontSize: '0.82rem', mt: 0.3 }}>
                      {fxAdjustedCommodity?.name} price in USD vs NGN equivalent (dual Y-axis)
                    </Typography>
                    <Typography sx={tsSx}>
                      Source: CBN × {fxAdjustedCommodity?.exchange} · Updated {stampDate}
                      {usingWorldReference && ' · monthly world average against the daily rate'}
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                    <InputLabel id="public-fx-adjusted-commodity-label" sx={selectLabelSx}>Commodity</InputLabel>
                    <Select
                      labelId="public-fx-adjusted-commodity-label"
                      label="Commodity"
                      value={fxAdjustedCommodityId}
                      onChange={(e) => setFxAdjustedCommodityId(e.target.value)}
                      sx={selectSx}
                    >
                      {(usingWorldReference
                        ? worldOptions.map((o) => ({ id: o.id, name: o.label, unit: unitLabelFor(o.unit, 'USD', worldUnit) }))
                        : globalCommodities
                      ).map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name} ({c.unit})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {usingWorldReference && (
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                      <InputLabel id="public-fx-adjusted-unit-label" sx={selectLabelSx}>Unit</InputLabel>
                      <Select
                        labelId="public-fx-adjusted-unit-label"
                        label="Unit"
                        value={worldUnit}
                        onChange={(e) => setWorldUnit(e.target.value)}
                        sx={selectSx}
                      >
                        {WORLD_UNITS.map((u) => (
                          <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                <Box sx={{ height: { xs: 320, md: 400 } }}>
                  {(correlationData && correlationData.length > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={correlationData}>
                        <CartesianGrid {...gridStyle} />
                        <XAxis dataKey="date" {...axisStyle} minTickGap={28} tickFormatter={fmtAxisDate} />
                        <YAxis
                          yAxisId="left"
                          {...axisStyle}
                          tickFormatter={(v) => {
                            if (typeof v !== 'number') return v;
                            return `$${v < 10 ? v.toFixed(2) : v.toFixed(0)}`;
                          }}
                          domain={['auto', 'auto']}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          {...axisStyle}
                          tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`}
                          domain={['auto', 'auto']}
                        />
                        <RechartsTooltip
                          cursor={{ stroke: palette.chartAxis, strokeWidth: 1, strokeDasharray: '3 3' }}
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0]?.payload;
                            if (!d) return null;
                            return (
                              <Box sx={ttBoxSx}>
                                <Typography sx={ttTitleSx}>{`${fxAdjustedCommodity?.name} · ${fmtDate(d.date)}`}</Typography>
                                <Typography sx={ttRowSx}>
                                  USD Price: ${d.usdPrice?.toFixed(2)}{fxAdjustedUnitSuffix ? `/${fxAdjustedUnitSuffix}` : ''}
                                </Typography>
                                <Typography sx={ttRowSx}>NGN Equivalent: ₦{d.ngnPrice?.toLocaleString()}</Typography>
                                <Typography sx={ttRowSx}>FX Rate: ₦{d.fxRate?.toLocaleString()}</Typography>
                              </Box>
                            );
                          }}
                        />
                        <Legend
                          {...legendProps}
                          formatter={(value) => <span style={{ color: palette.textSecondary, fontSize: 12 }}>{value}</span>}
                        />
                        {/* USD line = navy anchor; NGN line = green */}
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="usdPrice"
                          stroke={palette.navy}
                          strokeWidth={2}
                          name={`${fxAdjustedCommodity?.name} (USD)`}
                          dot={false}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="ngnPrice"
                          stroke={palette.accent}
                          strokeWidth={2}
                          name={`${fxAdjustedCommodity?.name} (NGN)`}
                          dot={false}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyState
                      compact
                      icon={<Icon icon="solar:chart-2-linear" width={24} height={24} />}
                      title="No data yet"
                      description="Data will appear here once published."
                      sx={{ height: { xs: 320, md: 400 } }}
                    />
                  )}
                </Box>
                  {/* CC BY 4.0 obliges the credit wherever the data shows. */}
                  {usingWorldReference && (
                    <WorldAttribution text={worldAttribution} sx={{ mt: 1.25 }} />
                  )}
              </Box>
            </MotionInView>
          </Container>
        </Box>
      )}

      {/* ==============================================================
          TAB 2 -- Commodity Price vs Dollar Rate (dual-axis, filterable)
          ============================================================== */}
      {activeTab === 2 && (
        <Box sx={{ bgcolor: palette.bg, py: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg">
            <MotionInView variants={fadeUp}>
              <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography sx={cardTitleSx}>{fxCommodityName} Price vs NGN/USD Rate</Typography>
                    <Typography sx={{ color: palette.textMuted, fontSize: '0.82rem', mt: 0.3 }}>
                      Dual-axis comparison - how commodity prices move relative to the exchange rate
                    </Typography>
                    <Typography sx={tsSx}>
                      {commodityVsFxData.some((d) => d.fxRate == null)
                        ? `Updated · ${stampDate} · CBN FX history starts ${fmtDate(fxRates[0]?.date)}, the rate line covers dates from there`
                        : `Updated · ${stampDate}`}
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                    <InputLabel id="public-fx-commodity-label" sx={selectLabelSx}>Commodity</InputLabel>
                    <Select
                      labelId="public-fx-commodity-label"
                      value={fxCommodity}
                      label="Commodity"
                      onChange={(e) => setFxCommodity(e.target.value)}
                      sx={selectSx}
                    >
                      {localCommoditiesWithData.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}{isRetired(c) ? ' (retired)' : ''}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {commodityVsFxData.length >= 2 ? (
                  <Box sx={{ height: { xs: 300, md: 400 } }}>
                    <ResponsiveContainer>
                      <ComposedChart data={commodityVsFxData}>
                        <CartesianGrid {...gridStyle} />
                        <XAxis dataKey="date" {...axisStyle} minTickGap={28} tickFormatter={fmtAxisDate} />
                        <YAxis
                          yAxisId="left"
                          {...axisStyle}
                          tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`}
                          label={{ value: `${fxCommodityName} (₦)`, angle: -90, position: 'insideLeft', style: { fill: palette.chartAxis, fontSize: 11, fontFamily: MONO } }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          {...axisStyle}
                          tickFormatter={(v) => `₦${v.toLocaleString()}`}
                          label={{ value: 'NGN/USD', angle: 90, position: 'insideRight', style: { fill: palette.chartAxis, fontSize: 11, fontFamily: MONO } }}
                        />
                        <RechartsTooltip
                          {...tt}
                          cursor={{ fill: palette.accentBg }}
                          labelFormatter={(d) => fmtDate(d)}
                          formatter={(value, name) =>
                            name === 'price'
                              ? [`₦${value.toLocaleString()}`, fxCommodityName]
                              : [`₦${value.toLocaleString()}`, 'NGN/USD Rate']
                          }
                        />
                        <Legend
                          {...legendProps}
                          formatter={(value) => (
                            <span style={{ color: palette.textSecondary, fontSize: 12 }}>
                              {value === 'price' ? fxCommodityName : 'NGN/USD Rate'}
                            </span>
                          )}
                        />
                        {/* Bar = local price (soft green); Line = FX rate (amber caution) */}
                        <Bar yAxisId="left" dataKey="price" fill={palette.accent} fillOpacity={0.7} radius={[4, 4, 0, 0]} name="price" />
                        {/* Weekly cadence + partial FX coverage: visible dots so a
                            week with only one FX-covered point still shows. */}
                        <Line yAxisId="right" type="monotone" dataKey="fxRate" stroke={palette.warning} strokeWidth={2.5} dot={{ r: 3.5, fill: palette.warning, strokeWidth: 0 }} activeDot={{ r: 5 }} name="fxRate" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <EmptyState
                    compact
                    icon={<Icon icon="solar:chart-2-linear" width={24} height={24} />}
                    title="Not enough price history yet"
                    description={`This view charts weekly ${fxCommodityName} prices against the CBN rate of the same week. It needs at least two weekly uploads in the selected range - try the ALL range or pick another commodity above.`}
                    sx={{ height: { xs: 300, md: 400 } }}
                  />
                )}
              </Box>
            </MotionInView>
          </Container>
        </Box>
      )}

      {/* ==============================================================
          CTA -- Premium access (F CTA card)
          ============================================================== */}
      <Box component="section" sx={{ py: { xs: 9, md: 12 }, bgcolor: palette.bg }}>
        <Container maxWidth="lg">
          <MotionInView variants={fadeUp}>
            <Box
              sx={{
                ...cardSx,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: `${radius.panel}px`,
                p: { xs: 4.5, md: 7 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 4,
              }}
            >
              <Box aria-hidden sx={{ position: 'absolute', right: -80, bottom: -120, width: 520, height: 360, pointerEvents: 'none', background: 'radial-gradient(closest-side, rgba(34,197,94,0.09), transparent)' }} />
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h2" sx={{ color: palette.text, fontSize: { xs: '1.8rem', md: '2.2rem' }, mb: 1.25 }}>
                  Go premium for the complete picture
                </Typography>
                <Typography sx={{ color: palette.textSecondary, fontSize: '1rem', maxWidth: 470, lineHeight: 1.6 }}>
                  Unlock detailed reports, custom date ranges, export tools, and deeper commodity intelligence with a premium account.
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.75, flexShrink: 0 }}>
                <Button component={RouterLink} to="/login?redirect=/insights" variant="contained" disableElevation endIcon={<ArrowForward sx={{ fontSize: 18, color: '#fff' }} />} sx={primaryBtnSx}>
                  Get premium access
                </Button>
              </Box>
            </Box>
          </MotionInView>
        </Container>
      </Box>
    </Box>
  );
}

export default function PublicInsights() {
  // Series lives here because the query key depends on it. placeholderData
  // keeps the previous series on screen while the new one loads, so switching
  // never drops the whole page back to the loading skeleton.
  const [fxSeriesKey, setFxSeriesKey] = useState('nfem');
  const fx = useFxSeries('ALL', FX_SERIES[fxSeriesKey].pair, {
    placeholderData: (prev) => prev,
  });
  const gc = useGlobalCommodities();
  const gp = useGlobalPrices('ALL');
  const c = useCommodities();
  const lp = useLocalPrices();
  if (fx.isLoading || gc.isLoading || gp.isLoading || c.isLoading || lp.isLoading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <PublicHero
          eyebrow="Currency Intelligence"
          title="Currency Analysis"
          subtitle="Loading FX and commodity price analysis for the Nigerian market…"
        />
        <LoadingSkeleton variant="page" contained />
      </Box>
    );
  }
  return (
    <PublicInsightsInner
      fxSeriesKey={fxSeriesKey}
      onSeriesChange={setFxSeriesKey}
      fxRates={fx.data || []}
      globalCommodities={gc.data || []}
      globalCommodityPrices={gp.data || {}}
      commodities={c.data || []}
      localPrices={lp.data || []}
    />
  );
}
